import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { filter, Observable, of, switchMap, tap } from 'rxjs';
import { RouteConstants } from '../../../../../../../core/constants/routes';
import { hasOpenDialogs } from '../../../../../../../core/functions/dialog-helper';
import { CheckmarkDialogComponent } from '../../../../../../../shared/components/checkmark-dialog/checkmark-dialog.component';
import { ModalComponent } from '../../../../../../../shared/components/modal/modal.component';
import {
    SubmissionToEwaAttachmentRequiredDialogMessage,
    SubmissionToEwaCompleteDialogMessage,
    SubmissionToEwaWithResultCompleteDialogMessage,
} from '../../../../../shared/constants/submission-dialog-message';
import { ApplicationStateService } from '../../../../../shared/services/application-state/application-state.service';
import { AttachmentResponse } from '../../../../shared/models/attachment/attachment-response';
import { AttachmentService } from '../../../../shared/services/attachment/attachment.service';
import { EwaSubmissionResponse } from '../models/ewa-submission-response';
import { SubmitToEwaCheckmarkDialogConfig } from './constants/submit-to-ewa-checkmark-dialog-config';

@Injectable({
    providedIn: 'root',
})
export class SubmitToEwaFormService {
    constructor(
        private readonly router: Router,
        private readonly dialog: MatDialog,
        private readonly attachmentService: AttachmentService,
        private readonly applicationStateService: ApplicationStateService,
    ) {}

    /**
     * Initiates the EWA submission process for cases where the API returns an EwaSubmissionResponse.
     * Shows an attachment-required dialog if needed, saves the form, and displays a context-aware success dialog on completion.
     *
     * @param saveFormToApi Callback to save the form data to the API (returns Observable<EwaSubmissionResponse>).
     * @param onAfterClose Callback to handle actions after the dialog is closed.
     */
    handoverToEwaSubmission(
        saveFormToApi: () => Observable<EwaSubmissionResponse | void>,
        onAfterClose: () => void,
    ): void {
        this.showAttachmentRequiredDialog()
            .pipe(
                // Only proceed with the submission if the user confirms (or if the dialog does not apply)
                filter(
                    (proceedWithSubmission: boolean) => proceedWithSubmission,
                ),
                switchMap(() => {
                    return saveFormToApi().pipe(
                        tap((response: EwaSubmissionResponse | void) => {
                            this.showSubmissionCompleteDialog(
                                response,
                                onAfterClose,
                            );
                        }),
                    );
                }),
            )
            .subscribe();
    }

    /**
     * Shows a dialog to the user after successful submission.
     *
     * @param response The response from the API.
     * @param onAfterClose Callback to handle actions after the dialog is closed.
     */
    private showSubmissionCompleteDialog(
        response: EwaSubmissionResponse | void,
        onAfterClose: () => void,
    ): void {
        if (this.shouldNavigateToOrderDetails(response)) {
            this.showCheckmarkDialog(
                {
                    ...SubmitToEwaCheckmarkDialogConfig,
                    data: {
                        title: SubmissionToEwaWithResultCompleteDialogMessage.modalTitle,
                        primaryAction: {
                            title: SubmissionToEwaWithResultCompleteDialogMessage.primaryActionTitle,
                        },
                    },
                },
                onAfterClose,
                () => this.navigateToOrderDetails(response!.orderId!),
            );
        } else {
            this.showCheckmarkDialog(
                {
                    ...SubmitToEwaCheckmarkDialogConfig,
                    data: {
                        title: SubmissionToEwaCompleteDialogMessage.modalTitle,
                        content:
                            SubmissionToEwaCompleteDialogMessage.modalContent,
                        primaryAction: {
                            title: SubmissionToEwaCompleteDialogMessage.primaryActionTitle,
                        },
                    },
                },
                onAfterClose,
                () => this.navigateToDashboard(),
            );
        }
    }

    /**
     * Determines whether to navigate to the order details page after successful submission.
     *
     * @param response The response from the API containing the order ID.
     * @returns True if the user should be navigated to the order details page, false otherwise.
     */
    private shouldNavigateToOrderDetails(
        response: EwaSubmissionResponse | void,
    ): boolean {
        if (response?.orderId == null) {
            return false;
        }

        return !this.applicationStateService.getCurrentApplicationState()
            ?.isCreatedOnBehalfOfCustomer;
    }

    /**
     * Shows a checkmark dialog with the provided title and action, then invokes the callback and navigation after closing.
     *
     * @param config Dialog configuration
     * @param onAfterClose Callback after dialog closes
     * @param onNavigate Callback to handle navigation after dialog closes
     */
    private showCheckmarkDialog(
        config: MatDialogConfig,
        onAfterClose: () => void,
        onNavigate: () => void,
    ): void {
        if (hasOpenDialogs(this.dialog)) {
            return;
        }

        const dialogRef = this.dialog.open(CheckmarkDialogComponent, config);

        dialogRef
            .afterClosed()
            .pipe(
                tap(() => {
                    onAfterClose();
                }),
                tap(() => {
                    onNavigate();
                }),
            )
            .subscribe();
    }

    /**
     * Redirects the user to the order details page for the given order ID.
     */
    private navigateToOrderDetails(orderId: number): void {
        this.router.navigate([
            RouteConstants.Order.getOrderSummaryRoute(orderId),
        ]);
    }

    /**
     * Redirects the user to the dashboard page.
     */
    private navigateToDashboard(): void {
        this.router.navigate([RouteConstants.Dashboard.getFullRoute('INDEX')]);
    }

    /**
     * Shows a dialog to the user if attachments are required but none are present.
     *
     * @return An Observable that emits true if the user wants to continue with the submission,
     *         or false if they choose to cancel (or does not apply).
     */
    private showAttachmentRequiredDialog(): Observable<boolean> {
        if (hasOpenDialogs(this.dialog)) {
            return of(false);
        }

        const applicationState =
            this.applicationStateService.getCurrentApplicationState();

        // If the application has been marked as not having attachments,
        // proceed directly with the submission to EWA
        if (!applicationState?.anyAttachments) {
            return of(true);
        }

        return this.attachmentService
            .getAll(applicationState.applicationId.toString())
            .pipe(
                switchMap((attachments: AttachmentResponse[]) => {
                    // If there are attachments, then we can proceed
                    if (attachments.length > 0) {
                        return of(true);
                    }

                    // Application is marked to contain attachments, but none are present
                    const dialogRef = this.dialog.open(ModalComponent, {
                        disableClose: true,
                        data: {
                            title: SubmissionToEwaAttachmentRequiredDialogMessage.modalTitle,
                            content:
                                SubmissionToEwaAttachmentRequiredDialogMessage.content,
                            primaryAction: {
                                title: SubmissionToEwaAttachmentRequiredDialogMessage.primaryActionTitle,
                                action: () => dialogRef.close(true), // Proceed with the submission to EWA
                            },
                            isShowCancel: true,
                        },
                    });

                    return dialogRef.afterClosed();
                }),
            );
    }
}
